import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "../../resourses/digitallabs/one-x-ui";
import DMQCustomCard from "../../components/common/DMQCustomCard";
import DynamicFormRenderer_Helix from "../../components/dynamicRendering/DyamicFormRenderer_Helix";

import {
  saveStepData,
  getStoredFormData,
  saveCurrentStep,
  clearCurrentInternalStep,
  saveCurrentInternalStep,
  getCurrentInternalStep,
  getEditingMode,
  setEditingMode,
  getQuestionBank,
  getDMQStepFlow,
  saveDMQStepFlow,
  getDMQMedicalConditions,
  saveDMQMedicalConditions,
  getUserNRIDetails,
} from "../../utils/formStorage";

import { useLoader } from "../../context/LoaderContext";
import { DMQStepNames, MAIN_STEPS } from "../../constants/constants";
import { checkUserInactivity } from "../../utils/checkUserInactivity";
import { withAuth } from "../../utils/withAuth";
import FormFooter from "../../components/common/FormFooter";
import SplashScreenModal from "../../components/common/SplashScreenModal";

const QUESTIONNAIRE_CONFIG = [
  {
    key: "isUserNRI",
    stepId: MAIN_STEPS.NRI_QUESTIONNAIRE,
    title: "NRI Questionnaire",
    condition: (medical, { storedConditions }) => {
      const userNRI = storedConditions?.isUserNRI ? storedConditions?.isUserNRI : false;
      return userNRI;
    },
  },
  {
    key: "isUserFN",
    stepId: MAIN_STEPS.FOREIGN_NATIONAL_QUESTIONNAIRE,
    title: "Foreign National Questionnaire",
    condition: (medical, { storedConditions }) => {
      const userFN = storedConditions?.isUserFN ? storedConditions?.isUserFN : false;
      return userFN;
    },
  },
  {
    key: "isDiabetic",
    stepId: MAIN_STEPS.DIABETIC_QUESTIONNAIRE,
    title: "Diabetic Questionnaire",
    condition: (medical) => medical?.diabetes === "Yes",
  },
  {
    key: "isBloodPressure",
    stepId: MAIN_STEPS.BLOOD_PRESSURE_QUESTIONNAIRE,
    title: "High BP Questionnaire",
    condition: (medical) => medical?.highBP === "Yes",
  },
  {
    key: "isThyroid",
    stepId: MAIN_STEPS.THYROID_QUESTIONNAIRE,
    title: "Thyroid Questionnaire",
    condition: (medical) => medical?.thyroidEndocrine === "Yes",
  },
  {
    key: "isKidney",
    stepId: MAIN_STEPS.KIDNEY_QUESTIONNAIRE,
    title: "Kidney Questionnaire",
    condition: (medical) => medical?.kidneyDisorder === "Yes",
  },
];

const StepMedicalHistory = ({ handleStepChange }) => {
  const { setLoading } = useLoader();
  const formRef = useRef(null);

  const [formState, setFormState] = useState({});
  const [renderingJSON, setRenderingJSON] = useState([]);

  const questionBank = getQuestionBank();

  const [internalStep, setInternalStep] = useState(getCurrentInternalStep() || 1);

  const totalInternalSteps = 3;

  const [isFormValid, setIsFormValid] = useState(false);

  const [showSplashModal, setShowSplashModal] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);

  const storedConditions = getDMQMedicalConditions();

  const [conditions, setConditions] = useState({
    isUserNRI: storedConditions?.isUserNRI ? storedConditions : false,
    isUserFN: storedConditions?.isUserFN ? storedConditions : false,
    isDiabetic: storedConditions?.isDiabetic ? storedConditions : false,
    isBloodPressure: storedConditions?.isBloodPressure ? storedConditions : false,
    isThyroid: storedConditions?.isThyroid ? storedConditions : false,
    isKidney: storedConditions?.isKidney ? storedConditions : false,
  });

  const handleChange = useCallback((name, value) => {
    setFormState((prev) => {
      if (Object.is(prev[name], value)) return prev;
      return {
        ...prev,
        [name]: value,
      };
    });
  }, []);

  /*
  HEADER BACK BUTTON SYNC
  */
  useEffect(() => {
    const syncInternalStep = (event) => {
      if (event.detail?.internalStep) {
        setInternalStep(event.detail.internalStep);
      }
    };

    window.addEventListener("dmq-step-change", syncInternalStep);

    return () => {
      window.removeEventListener("dmq-step-change", syncInternalStep);
    };
  }, []);

  /*
  QUESTION RENDER
  */
  const renderQuestionBank = (step) => {
    switch (step) {
      case 1:
        setRenderingJSON(questionBank[DMQStepNames.GENERAL_HISTORY]);
        break;

      case 2:
        setRenderingJSON(questionBank[DMQStepNames.MEDICAL_HISTORY]);
        break;

      case 3:
        setRenderingJSON(questionBank[DMQStepNames.INSURANCE_HISTORY]);
        break;

      default:
        setRenderingJSON([]);
    }
  };

  /*
  LOAD STEP DATA
  */
  useEffect(() => {
    setLoading(true);

    saveCurrentStep(MAIN_STEPS.MEDICAL_HISTORY);

    const stored = getStoredFormData() || {};

    renderQuestionBank(internalStep);

    if (internalStep === 1 && stored[DMQStepNames.GENERAL_HISTORY]) {
      setFormState(stored[DMQStepNames.GENERAL_HISTORY]);
    } else if (internalStep === 2 && stored[DMQStepNames.MEDICAL_HISTORY]) {
      setFormState(stored[DMQStepNames.MEDICAL_HISTORY]);
    } else if (internalStep === 3 && stored[DMQStepNames.INSURANCE_HISTORY]) {
      setFormState(stored[DMQStepNames.INSURANCE_HISTORY]);
    } else {
      setFormState({});
    }

    setLoading(false);
  }, [internalStep]);

  /*
  SCROLL TOP
  */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [internalStep]);

  const onFinish = (values) => {
    checkUserInactivity(null, async () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setLoading(true);

      const stepConfig = {
        1: { name: DMQStepNames.GENERAL_HISTORY },
        2: { name: DMQStepNames.MEDICAL_HISTORY },
        3: { name: DMQStepNames.INSURANCE_HISTORY },
      };

      const config = stepConfig[internalStep];
      const isEditing = getEditingMode();

      if (!config) {
        setLoading(false);
        return;
      }

      //  Save current step data
      saveStepData(config.name, values);

      /**
       * ============================
       * MEDICAL HISTORY (DYNAMIC)
       * ============================
       */
      if (config.name === DMQStepNames.MEDICAL_HISTORY) {
        const medical = values || {};

        //  Build conditions dynamically
        const computedConditions = {};

        QUESTIONNAIRE_CONFIG.forEach((q) => {
          computedConditions[q.key] = q.condition(medical, { storedConditions: getDMQMedicalConditions() });
        });

        setConditions(computedConditions);
        saveDMQMedicalConditions(computedConditions);

        let stepFlow = getDMQStepFlow();

        //  Insert steps dynamically
        QUESTIONNAIRE_CONFIG.forEach(({ key, stepId, title }) => {
          const shouldAdd = computedConditions[key];
          const alreadyExists = stepFlow.some((step) => step.id === stepId);

          if (shouldAdd && !alreadyExists) {
            const confirmIndex = stepFlow.findIndex((step) => step.id === MAIN_STEPS.CONFIRM_DETAILS);

            stepFlow.splice(confirmIndex, 0, {
              id: stepId,
              title,
              internalSteps: 0,
            });
          }
        });

        saveDMQStepFlow(stepFlow);

        /**
         * ============================
         * EDIT MODE FLOW
         * ============================
         */
        if (isEditing) {
          //  Clear false condition data
          QUESTIONNAIRE_CONFIG.forEach(({ key, stepId }) => {
            if (!computedConditions[key]) {
              saveStepData(stepId, {});
            }
          });

          //  Determine next step (priority order)
          const nextStepConfig = QUESTIONNAIRE_CONFIG.find(({ key }) => computedConditions[key]);

          const nextStep = nextStepConfig ? nextStepConfig.stepId : MAIN_STEPS.CONFIRM_DETAILS;

          handleStepChange(nextStep);

          setEditingMode(false);
          clearCurrentInternalStep();
          setLoading(false);
          return;
        }
      }

      /**
       * ============================
       * INSURANCE HISTORY EXIT
       * ============================
       */
      if (config.name === DMQStepNames.INSURANCE_HISTORY) {
        const stepFlow = getDMQStepFlow();
        const currentIndex = stepFlow.findIndex((step) => step.id === MAIN_STEPS.MEDICAL_HISTORY);

        const nextStep = stepFlow[currentIndex + 1];
        const isEditing = getEditingMode();

        const storedConditions = getDMQMedicalConditions();
        //console.log("Stored Conditions on Insurance History Finish:", storedConditions);

        const hasAnyCondition = QUESTIONNAIRE_CONFIG.some(({ key }) => storedConditions?.[key]);
        //console.log("Has Any Condition:", hasAnyCondition);
        //console.log("QUESTIONNAIRE_CONFIG", QUESTIONNAIRE_CONFIG);

        //  SHOW MODAL (NON-EDIT MODE)
        if (!isEditing && hasAnyCondition) {
          const nextStepConfig = QUESTIONNAIRE_CONFIG.find(({ key }) => storedConditions?.[key]);
          //console.log("Next Step Config for Modal:", nextStepConfig);

          setPendingStep(nextStepConfig?.stepId);
          setShowSplashModal(true);

          setLoading(false);
          return;
        }

        //  DEFAULT FLOW
        if (nextStep) {
          handleStepChange(nextStep.id);
        }

        clearCurrentInternalStep();
        setEditingMode(false);
        setLoading(false);
        return;
      }

      /**
       * ============================
       * NEXT INTERNAL STEP
       * ============================
       */
      const nextInternal = internalStep + 1;

      setInternalStep(nextInternal);
      saveCurrentInternalStep(nextInternal);

      renderQuestionBank(nextInternal);
      setFormState({});
      setLoading(false);
    });
  };

  const onValidationChange = useCallback((isValid) => {
    setIsFormValid((prev) => (prev === isValid ? prev : isValid));
  }, []);

  const handleSplashContinue = () => {
    if (pendingStep) {
      handleStepChange(pendingStep);
    }

    setShowSplashModal(false);
    setPendingStep(null);
    setConditions({
      isUserNRI: false,
      isUserFN: false,
      isDiabetic: false,
      isBloodPressure: false,
      isThyroid: false,
      isKidney: false,
    });

    clearCurrentInternalStep();
  };

  return (
    <div style={{ paddingBottom: "80px" }}>
      <DMQCustomCard
        title={internalStep === 1 ? "General Health Question" : internalStep === 2 ? "Medical History" : "Insurance Medical History"}
        subtitle={
          internalStep === 1
            ? "Please answer a few quick questions about your health"
            : internalStep === 2
            ? "Please answer a few quick questions about your medical history"
            : "Please answer a few quick questions about your insurance medical history"
        }
        pageHeader={internalStep === 2 ? "Have you ever suffered in the past or are you currently suffering from:" : null}
        totalDots={totalInternalSteps}
        activeDot={internalStep}
      >
        <DynamicFormRenderer_Helix
          ref={formRef}
          step={MAIN_STEPS.MEDICAL_HISTORY}
          internalStep={internalStep}
          schema={renderingJSON}
          formState={formState}
          handleChange={handleChange}
          onFormFinish={() => onFinish(formState)}
          onFormValidityChange={onValidationChange}
        />
      </DMQCustomCard>

      <FormFooter>
        <Button
          variant="primary"
          size="md"
          fullWidth
          className={!isFormValid ? "next-btn-disabled" : ""}
          onClick={() => formRef.current?.triggerNext()}
        >
          {getEditingMode() ? "Confirm" : "Next"}
        </Button>
      </FormFooter>

      <SplashScreenModal visible={showSplashModal} conditions={conditions} onContinue={handleSplashContinue} />
    </div>
  );
};

export default withAuth(StepMedicalHistory);
